import { useTheme } from '@mui/material';
import Highcharts, { ColorString, GradientColorObject, PatternObject } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsTreemap from 'highcharts/modules/treemap';
import { useEffect, useRef } from 'react';

HighchartsTreemap(Highcharts);

interface IProps {
  data: Array<number | null | Highcharts.PointOptionsObject>;
  dataLabelFormatter: Highcharts.DataLabelsFormatterCallbackFunction;
  tooltipFormatter: Highcharts.TooltipFormatterCallbackFunction;
  height: number;
  colors: Array<ColorString | GradientColorObject | PatternObject>;
  loading?: boolean;
}

const TreemapChart = ({ data, dataLabelFormatter, tooltipFormatter, height = 300, colors, loading }: IProps) => {
  const {
    palette: { commonBlackStatesMain },
    shape,
  } = useTheme();

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    if (!chartComponentRef.current?.chart) return;
    if (loading) {
      chartComponentRef.current?.chart.showLoading('Loading data...');
      return;
    }

    chartComponentRef.current?.chart.hideLoading();
  }, [chartComponentRef.current?.chart, loading]);

  const options: Highcharts.Options = {
    chart: {
      height: height,
      margin: 0,
    },
    plotOptions: {
      series: {
        borderWidth: 0,
        states: {
          hover: {
            enabled: false, // Disable hover effect
          },
        },
        dataLabels: {
          enabled: true,
          align: 'center',
          formatter: dataLabelFormatter,
          style: {
            textOutline: 'none',
            lineHeight: 20,
            textAlign: 'center',
          },
        },
      },
    },
    tooltip: {
      style: {
        color: '#fff',
        lineHeight: '20px',
        fontSize: '12px',
      },
      valueDecimals: 1,
      backgroundColor: commonBlackStatesMain.main,
      padding: 14,
      borderRadius: shape.borderRadius * 2,
      useHTML: true,
      formatter: tooltipFormatter,
    },
    colors: colors,
    series: [
      {
        type: 'treemap',
        layoutAlgorithm: 'squarified',
        clip: false,
        layoutStartingDirection: 'vertical',
        colorByPoint: true,
        data: data,
      } as Highcharts.SeriesTreemapOptions,
    ],
    title: {
      text: '',
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} ref={chartComponentRef} />;
};

export default TreemapChart;
